import React from 'react';
import AppModal from '../../common/AppModal';

export interface ExportConfirmDialogProps {
  isOpen: boolean;
  count: number | null;
  large: boolean;
  loadingCount: boolean;
  errorMessage?: string | null;
  entityLabel?: string;
  title?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ExportConfirmDialog: React.FC<ExportConfirmDialogProps> = ({
  isOpen,
  count,
  large,
  loadingCount,
  errorMessage,
  entityLabel,
  title,
  onConfirm,
  onCancel,
}) => {
  const confirmBtnRef = React.useRef<HTMLButtonElement>(null);
  const cancelBtnRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (!isOpen) return;
    // Escape is AppModal's now (#968) — it called onCancel, which is this dialog's
    // own onClose. Enter stays here; nothing else handles it.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && count != null && !loadingCount && !errorMessage) onConfirm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, count, loadingCount, errorMessage, onConfirm, onCancel]);

  // Focus Confirm when ready; Cancel while loading/error/empty so Enter still does something.
  React.useEffect(() => {
    if (!isOpen) return;
    const target = (loadingCount || errorMessage || count == null || count === 0)
      ? cancelBtnRef.current
      : confirmBtnRef.current;
    target?.focus();
  }, [isOpen, loadingCount, errorMessage, count]);

  if (!isOpen) return null;

  const noun = entityLabel ?? 'records';
  const formattedCount = count != null ? count.toLocaleString() : '…';
  const canConfirm = !loadingCount && !errorMessage && count != null && count > 0;

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onCancel}
      width={420}
      persistent
      showClose={false}
    >
      <div style={{
        fontSize: 'var(--em3-font-size-xl)',
        fontWeight: 600,
        marginBottom: 'var(--em3-spacing-md)',
      }}>
        {title ?? 'Confirm export'}
      </div>
      <div style={{ margin: 0 }}>
        {loadingCount && <div>Counting matching {noun}…</div>}
        {!loadingCount && errorMessage && (
          <div style={{ color: 'var(--em3-color-danger)' }}>{errorMessage}</div>
        )}
        {!loadingCount && !errorMessage && count === 0 && (
          <div>No {noun} match the current filters.</div>
        )}
        {!loadingCount && !errorMessage && count != null && count > 0 && (
          <>
            <div>Export <strong>{formattedCount}</strong> {noun} matching the current filters?</div>
            {large && (
              <div style={{ color: 'var(--em3-color-warning)', fontSize: 'var(--em3-font-size-sm)', marginTop: 'var(--em3-spacing-sm)' }}>
                This is a large export. It will be queued and you'll be notified when it's ready.
              </div>
            )}
          </>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--em3-spacing-sm)', marginTop: 'var(--em3-spacing-lg)', marginBottom: 'var(--em3-spacing-sm)' }}>
        <button ref={cancelBtnRef} type="button" className="btn btn-neutral" onClick={onCancel}>
          Cancel
        </button>
        <button ref={confirmBtnRef} type="button" className="btn btn-primary" onClick={onConfirm} disabled={!canConfirm}>
          Confirm
        </button>
      </div>
    </AppModal>
  );
};

export default ExportConfirmDialog;
